import { useCallback, useEffect, useRef } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { useBlocker } from 'react-router-dom';

interface UnsavedChangesBlockerOptions {
    when: boolean | (() => boolean);
    onDiscard: () => void;
    message?: string;
}

/** Блокирует SPA-навигацию и перезагрузку при несохранённых изменениях */
export const useUnsavedChangesBlocker = (
    {
        when,
        onDiscard,
        message = 'У вас есть несохранённые изменения. Хотите уйти? Изменения будут потеряны',
    }: UnsavedChangesBlockerOptions
): void => {
    const shouldBlock = useCallback(() => typeof when === 'function'
        ? when()
        : when, [when]);

    const blocker = useBlocker(shouldBlock);
    const enabled = shouldBlock();
    const onDiscardRef = useRef(onDiscard);
    onDiscardRef.current = onDiscard;

    useEffect(() => {
        if (!enabled) return;

        /** Предупреждает о потере изменений при закрытии страницы */
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!shouldBlock()) return;
            event.preventDefault();
            event.returnValue = 'true';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [enabled, shouldBlock]);

    useEffect(() => {
        if (blocker.state !== 'blocked') return;

        let settled = false;

        /** Оставляет сотрудника на странице с изменениями */
        const stay = () => {
            if (settled) return;
            settled = true;
            blocker.reset();
        };

        const dialog = confirmDialog({
            header: 'Несохранённые изменения',
            message,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Уйти',
            rejectLabel: 'Остаться',
            acceptClassName: 'p-button-danger',
            defaultFocus: 'reject',
            accept: () => {
                if (settled) return;
                settled = true;
                onDiscardRef.current();
                blocker.proceed();
            },
            reject: stay,
            onHide: stay,
        });

        return () => {
            settled = true;
            dialog?.hide();
        };
    }, [blocker, message]);
};
